import React from 'react';
import { type File as ParsedDiffFile } from '@/shared/types/parse-diff';
import { Card } from '@/ui/Card';
import VirtualizedUnifiedList from './VirtualizedUnifiedList';
import FileHeader from './FileHeader';

interface FileDiffProps {
  file: ParsedDiffFile;
}

const FileDiff: React.FC<FileDiffProps> = ({ file }) => {
  const filePath = file.to || file.from || '';

  // Calculate the approximate height of the content to make the container adjustable.
  const totalLines =
    file.chunks?.reduce(
      (acc, chunk) => acc + chunk.changes.length + 1, // +1 for each chunk header
      0
    ) ?? 0;

  const AVG_LINE_HEIGHT = 17; // A slight overestimate to account for hunk headers being larger
  const MAX_HEIGHT = 384; // Corresponds to `h-96` (24rem)

  const contentHeight = totalLines * AVG_LINE_HEIGHT;
  // Use the calculated height, but cap it at MAX_HEIGHT.
  const containerHeight = Math.min(contentHeight, MAX_HEIGHT);

  return (
    <Card className="overflow-hidden font-mono text-sm border-border">
      <FileHeader file={file} />
      {file.binary ? (
        <div className="p-4 text-sm text-muted-foreground">
          Binary file not shown.
        </div>
      ) : file.chunks && file.chunks.length > 0 ? (
        <div style={{ height: `${containerHeight}px`, minHeight: '48px' }}>
          <VirtualizedUnifiedList chunks={file.chunks} filePath={filePath} />
        </div>
      ) : (
        <div className="p-4 text-sm text-muted-foreground">
          No content changes. This may be a file mode change or an empty file.
        </div>
      )}
    </Card>
  );
};

export default FileDiff;
